// src/pages/ebilling/EmailFormPage.js

import { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
    Container,
    Box,
    TextField,
    Button,
    Typography,
    Paper,
    Alert,
    CircularProgress,
    Stack
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { apiEbilling } from '../../api/ebilling';
import { VerifyEmailContext } from '../../auth/AuthContext';

const SHOW_SKIP_CONSENT_BUTTON = process.env.REACT_APP_SHOW_SKIP_CONSENT;
const EmailFormPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const auth = useContext(VerifyEmailContext);
    if (!auth) throw new Error('AuthContext is undefined');
    const { login } = auth;
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [messageType, setMessageType] = useState('info');
    const [customerId, setCustomerId] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [isEmailEditable, setIsEmailEditable] = useState(false);
    const [isEditingEmail, setIsEditingEmail] = useState(false);
    const [showPointDetailFromUrl, setShowPointDetailFromUrl] = useState("");

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const customerId = params.get('customerId');
        const showPointDetailFromUrl = params.get("showPointDetail");
        setShowPointDetailFromUrl(showPointDetailFromUrl);

        if (!customerId) {
            setMessage('ไม่พบข้อมูล Customer ID กรุณาเข้าสู่ระบบใหม่');
            setMessageType('error');
            return;
        }
        const firstName = params.get('firstName');
        const lastName = params.get('lastName');
        setEmail(params.get('email') || '');
        setCustomerId(customerId);
        setFirstName(firstName);
        setLastName(lastName);
    }, [location.search]);

    // Query to get consent data
    const {
        data: conEBillingData,
        isLoading: isLoadingConsent,
        error: consentError,
        isSuccess: isSuccessConsent
    } = useQuery({
        queryKey: ['consentEBilling', customerId],
        queryFn: () => apiEbilling.getConsentEBilling(customerId),
        enabled: !!customerId,
        retry: 1
    });

    // --- New Code for Handling createConsent and sendOtp ---

    // Mutation for sending OTP
    const sendOtpMutation = useMutation({
        mutationFn: apiEbilling.createVerifyOTPEmail,
        onSuccess: (response) => {
            login({
                customerId,
                email,
            });

            if (response.success) {
                navigate(`/verify-email?customerId=${customerId}&showPointDetail=${showPointDetailFromUrl}`, {
                    state: {
                        email: email,
                        customerId: customerId,
                        otpRef: response.data.data?.otp_ref || '',
                        expireTime: response.data.data?.expire_time || null,
                        otpId: response.data.data?.id || '',
                        phoneNumber: response.data.data?.phone_number || '',
                        firstName: firstName,
                        lastName: lastName,
                    }
                });
            } else {
                setMessage('เกิดข้อผิดพลาดในการส่งรหัส OTP');
                setMessageType('error');
            }
        },
        onError: (error) => {
            navigate(`/close?customerId=${customerId}&status=false&msgErr=${error.message || 'เกิดข้อผิดพลาดในการส่ง OTP'}`);
        },
    });

    // Mutation for creating consent e-billing
    const createConsentMutation = useMutation({
        mutationFn: async (payload) => {
            return await apiEbilling.createConsentEBilling(payload);
        },
        onSuccess: (response) => {
            if (response.success) {
                // Consent created successfully, now send OTP
                sendOtpMutation.mutate({
                    email: email,
                    owner: 'ebilling-consent',
                    customer_id: customerId,
                    firstName: firstName,
                    lastName: lastName,
                });
            } else {
                // API call successful but response was not a success
                setMessage('เกิดข้อผิดพลาดในการบันทึกข้อมูล e-billing');
                setMessageType('error');
            }
        },
        onError: (error) => {
            // Navigate to error page when createConsentEBilling fails
            navigate(`/close?customerId=${customerId}&status=false&msgErr=${error.message || 'เกิดข้อผิดพลาดในการสร้าง e-billing'}`);
        },
    });
    const updateConsentMutation = useMutation({
        mutationFn: async (payload) => {
            return await apiEbilling.updateConsentEBilling(payload);
        },
        onSuccess: (response) => {
            if (response.success) {
                // Update successful, now send OTP
                sendOtpMutation.mutate({
                    email: email,
                    owner: 'ebilling-consent',
                    customer_id: customerId,
                    firstName: firstName,
                    lastName: lastName,
                });
            } else {
                setMessage('เกิดข้อผิดพลาดในการแก้ไขอีเมล');
                setMessageType('error');
            }
        },
        onError: (error) => {
            navigate(`/close?customerId=${customerId}&status=false&msgErr=${error.message || 'เกิดข้อผิดพลาดในการแก้ไขอีเมล'}`);
        },
    });

    // Handle getConsentEBilling response
    useEffect(() => {
        if (isSuccessConsent && conEBillingData) {
            if (conEBillingData.success && conEBillingData.data?.email) {
                // Email found in system - prefill and disable editing
                setEmail(conEBillingData.data.email);
                setFirstName(conEBillingData.data.firstName);
                setLastName(conEBillingData.data.lastName);
                setIsEmailEditable(false);
                setMessageType('info');
            } else {
                // No email found in system - allow user input
                setEmail('');
                setFirstName(conEBillingData.data?.firstName || '');
                setLastName(conEBillingData.data?.lastName || '');
                setIsEmailEditable(true);
                setMessage('ไม่พบอีเมลในระบบ กรุณากรอกอีเมลที่ถูกต้อง');
                setMessageType('warning');
            }
        }
    }, [isSuccessConsent, conEBillingData]);

    // Handle getConsentEBilling API error
    useEffect(() => {
        if (consentError) {
            // Check for specific 404 error code
            if (consentError.statusCode === "404" || consentError.message === "404") {
                // Set the component to "create mode"
                setEmail('');
                setIsEmailEditable(true);
                setMessage('ไม่พบข้อมูลอีเมลในระบบ กรุณากรอกอีเมลที่ต้องการลงทะเบียน');
                setMessageType('warning');
                return;
            }
            // For any other error, navigate to the error page
            navigate(`/close?customerId=${customerId}&status=false&msgErr=${encodeURIComponent(consentError.message || 'เกิดข้อผิดพลาดในการโหลดข้อมูล')}`);
        }
    }, [consentError, navigate, customerId]);


    const handleSubmit = (e) => {
        e.preventDefault();

        if (!email) {
            setMessage('กรุณากรอกอีเมล');
            setMessageType('error');
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            setMessage('รูปแบบอีเมลไม่ถูกต้อง');
            setMessageType('error');
            return;
        }

        setMessage('');
        setMessageType('info');

        if (isEditingEmail) {
            // Call update API if editing
            updateConsentMutation.mutate({
                customerId: customerId,
                email: email,
            });
        } else if (isEmailEditable) {
            // Create consent if no email found
            createConsentMutation.mutate({
                customerId: customerId,
                email: email,
                firstName: firstName,
                lastName: lastName,
            });
        } else {
            // Send OTP if email found and not editing
            sendOtpMutation.mutate({
                email: email,
                owner: 'ebilling-consent',
                customer_id: customerId,
                firstName: firstName,
                lastName: lastName,
            });
        }
    };

    const handleEditEmail = () => {
        setIsEmailEditable(true);
        setIsEditingEmail(true);
        setMessageType('info');
    };

    // Show loading state while fetching consent data
    if (isLoadingConsent) {
        return (
            <Container
                component="main"
                maxWidth="sm"
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    p: 2,
                }}
            >
                <CircularProgress size={48} />
                <Typography sx={{ mt: 2 }}>กำลังโหลดข้อมูลลูกค้า...</Typography>
            </Container>
        );
    }

    const isPending =
        sendOtpMutation.isPending ||
        createConsentMutation.isPending ||
        updateConsentMutation.isPending;

    return (
        <Container
            component="main"
            maxWidth={false}
            disableGutters
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: { xs: 'flex-start', sm: 'center' },
                minHeight: '100vh',
                p: { xs: 0, sm: 2 },
                bgcolor: { xs: 'white', sm: '#f5f7fa' }, // Professional background for browser
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    borderRadius: { xs: 0, sm: 3 },
                    width: '100%',
                    maxWidth: { xs: 'none', sm: 450 }, // 9:16 Aspect Ratio approximation
                    minHeight: { xs: '100vh', sm: 800 }, // 9:16 Aspect Ratio approximation
                    boxSizing: 'border-box',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch', // Always stretch to fill card width
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    boxShadow: { xs: 'none', sm: '0 8px 24px rgba(0,0,0,0.05)' } // Shadow for browser card
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        bgcolor: 'white',
                        p: { xs: 3, sm: 6 }, // Balanced vertical space in header (48px)
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexDirection: 'column',
                    }}
                >
                    <Box
                        component="img"
                        src="/image/logo-header.png"
                        alt="SG CAPITAL"
                        sx={{
                            height: 60,
                            mb: 2,
                            objectFit: 'contain'
                        }}
                    />
                    <Typography
                        variant="h5"
                        component="h1"
                        sx={{
                            fontWeight: 500,
                            color: '#666666',
                            fontSize: '1.5rem'
                        }}
                    >
                        Verify Email
                    </Typography>
                </Box>
                <Box
                    component="form"
                    onSubmit={handleSubmit}
                    sx={{
                        px: { xs: 2.5, sm: 6 }, // Increased horizontal padding for browser
                        pb: { xs: 5, sm: 6 }, // Consistent bottom padding
                        width: '100%',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <Stack spacing={{ xs: 4, sm: 5 }}>
                            <Box sx={{ position: 'relative' }}>
                                <TextField
                                    fullWidth
                                    label="E-mail"
                                    variant="outlined"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    placeholder="กรุณาใส่ E-mail"
                                    disabled={!isEmailEditable && isSuccessConsent && email !== ''}
                                    slotProps={{
                                        input: {
                                            endAdornment: !isEmailEditable && !isLoadingConsent && email !== '' && (
                                                <Stack direction="row" alignItems="center">
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={handleEditEmail}
                                                        sx={{
                                                            minWidth: 'auto',
                                                            whiteSpace: 'nowrap',
                                                            px: 1.5,
                                                            py: 0.5,
                                                            borderRadius: 2,
                                                            borderColor: '#d0d0d0',
                                                            color: '#666',
                                                            fontSize: '0.875rem',
                                                            '&:hover': { borderColor: '#999', backgroundColor: '#f5f5f5' }
                                                        }}
                                                        startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                                                    >
                                                        แก้ไข
                                                    </Button>
                                                </Stack>
                                            )
                                        }
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: '#f8f9fa',
                                            height: 64,
                                            '& fieldset': { borderColor: '#e0e0e0' },
                                            '&:hover fieldset': { borderColor: '#c0c0c0' },
                                            '&.Mui-focused fieldset': { borderColor: '#1976d2' },
                                        },
                                        '& .MuiInputLabel-root': { fontSize: '1.1rem', '&.Mui-required::after': { color: 'red' } },
                                        '& .MuiOutlinedInput-input': { fontSize: '1.1rem', padding: '18px 14px' }
                                    }}
                                />
                            </Box>
                        </Stack>

                        {message && (
                            <Box sx={{ mt: 2 }}>
                                <Alert severity={messageType}>{message}</Alert>
                            </Box>
                        )}
                        <Box sx={{ flex: 1 }} />
                    </Box>

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={{
                            borderRadius: 3,
                            backgroundColor: '#2196f3',
                            fontSize: '1.2rem',
                            fontWeight: 500,
                            textTransform: 'none',
                            boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                            height: 64, // Standardized height
                            '&:hover': { backgroundColor: '#1976d2', boxShadow: '0 6px 16px rgba(33, 150, 243, 0.4)' },
                            '&:disabled': { backgroundColor: '#ccc', boxShadow: 'none' }
                        }}
                        disabled={isPending || !email || (!isEmailEditable && email === '')}
                    >
                        {isPending ? (
                            <CircularProgress size={24} color="inherit" />
                        ) : (
                            'ถัดไป'
                        )}
                    </Button>
                    {SHOW_SKIP_CONSENT_BUTTON && (
                        <Box
                            sx={{
                                mt: 2,
                                py: 1.5,
                                textAlign: 'center',
                            }}
                        >
                            <Box
                                component="a"
                                href={`/close?customerId=${customerId}&status=skip`}
                                sx={{
                                    display: 'inline',
                                    color: '#666',
                                    textDecoration: 'none',
                                    fontSize: '0.9rem',
                                    transition: 'all 0.2s',
                                    padding: '4px 8px',
                                    borderRadius: 1,
                                    '&:hover': {
                                        backgroundColor: '#f5f5f5',
                                    },
                                    '&:active': {
                                        backgroundColor: '#e0e0e0',
                                    },
                                    cursor: 'pointer'
                                }}
                            >
                                ไม่ใช่ตอนนี้
                            </Box>
                        </Box>
                    )}
                </Box>
            </Paper>
        </Container>
    );
};

export default EmailFormPage;